/**
 * `SearchTrigger` — the topbar "Search or jump to…" field, and the combobox
 * that owns the whole search interaction.
 *
 * ── OPENS IN PLACE (founder correction) ────────────────────────────────────
 * This shipped once as a centered modal behind a full-screen scrim. That is the
 * right pattern for a ⌘K palette with NO visible trigger, where the overlay is
 * what announces the mode change. We have a visible control that already looks
 * like a search field, so clicking it and getting a SECOND search field in the
 * middle of a dimmed page is a bait-and-switch — you end up typing somewhere
 * other than where you clicked.
 *
 * Now: the box IS the input. Results drop below it as an anchored popover. No
 * scrim, no repositioning, no second input. Nothing about the box's rendered
 * resting appearance changed in the process — see the visual contract below.
 *
 * ── VISUAL CONTRACT — DO NOT RESTYLE ───────────────────────────────────────
 * Canon from `design/v2-direction/index.html` (.cmdk): 256×34, 1px border,
 * `--radius`, gap 8px, padding 0 10px, 13px text, ⌘K chip at 10.5px/600 in a
 * 5px-radius subtle-surface box. Preserved verbatim across BOTH rewrites.
 *
 * The `<span>` that held the placeholder text is now an `<input>`, so three
 * resets are load-bearing and must not be dropped:
 *   - `bg-transparent` — a bare input's UA background is the opaque `field`
 *     colour, which would paint a pale rectangle inside the control and be
 *     glaring in dark mode.
 *   - `placeholder-text-secondary` — the colour the old span inherited, so the
 *     resting state stays pixel-identical.
 *   - `p-0` — preflight zeroes input padding, but stating it stops a future
 *     utility reintroducing the UA inset.
 * The focus ring moved to the wrapper via `focus-within`, so focusing the input
 * lights the whole control instead of drawing a second ring inside it.
 *
 * ── ACCESSIBILITY ──────────────────────────────────────────────────────────
 * WAI-ARIA combobox/listbox. The input is `role="combobox"` with
 * `aria-expanded` / `aria-controls` / `aria-activedescendant`; the popover is
 * the listbox it points at. There is NO `role="dialog"` and NO `aria-modal`:
 * the page behind is genuinely still live, and claiming otherwise would be a
 * lie to assistive tech.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTenantBase } from "@/hooks/useTenantBase";
import { type PaletteRow } from "@/components/v2/search/useSearchResults";
import {
  LISTBOX_ID,
  OPTION_ID_PREFIX,
  SearchResultsController,
} from "@/components/v2/search/SearchResultsPanel";

/** Settle time before a keystroke becomes a server request. */
const DEBOUNCE_MS = 200;

export function SearchTrigger(): JSX.Element {
  const base = useTenantBase();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  // Lifted from the controller, which is the only thing that can compute them.
  // Held here because keyboard navigation belongs to the input.
  const [flatRows, setFlatRows] = useState<PaletteRow[]>([]);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce only the SERVER term; the client groups read `query` directly so
  // typing feels instant and only the network waits.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  // Clamp the cursor as results change under it; reset to the top when the
  // query itself changes, since a new query means a genuinely new list.
  useEffect(() => {
    setActiveIndex((i) => (flatRows.length === 0 ? 0 : Math.min(i, flatRows.length - 1)));
  }, [flatRows.length]);
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  /**
   * Close AND clear.
   *
   * The modal could just unmount and forget its state. This input is mounted
   * permanently in the chrome, so a dismissed query would sit in the topbar on
   * every page until manually deleted. Esc on a search field conventionally
   * means "never mind", so it resets the control to its placeholder rather
   * than leaving debris in the shell.
   */
  const close = useCallback((): void => {
    setOpen(false);
    setQuery("");
    setDebounced("");
    setFlatRows([]);
  }, []);

  const go = useCallback(
    (href: string) => {
      setOpen(false);
      setQuery("");
      setDebounced("");
      setFlatRows([]);
      // Focus stays on the topbar input — it is where the user was and it is
      // still on screen after navigating. This IS the focus restoration: with
      // an in-place combobox there is no separate trigger to return to.
      inputRef.current?.focus();
      navigate(href);
    },
    [navigate],
  );

  const selectRow = useCallback((row: PaletteRow) => go(row.href), [go]);

  // ⌘K / Ctrl+K from anywhere, including inside other text inputs. A modifier
  // chord cannot be mistaken for typing, and the fields a guard would disable
  // it in (the All-Candidates filter, the Actions Log search) are exactly where
  // someone reaches for it. `preventDefault` beats the browser's own ⌘K.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent): void {
      if (e.key !== "k" && e.key !== "K") return;
      if (!e.metaKey && !e.ctrlKey) return;
      if (e.altKey) return;
      e.preventDefault();
      setOpen(true);
      // Land the caret where the user is about to type, even when the chord
      // fired from a different input. Selecting any existing text means a
      // second ⌘K retypes cleanly rather than appending.
      inputRef.current?.focus();
      inputRef.current?.select();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Click outside closes. `mousedown`, not `click`, so the popover is gone
  // before a click on the page behind resolves; result rows preventDefault on
  // their own mousedown so selecting one is not seen as an outside click.
  useEffect(() => {
    if (!open) return undefined;
    function onDocMouseDown(e: MouseEvent): void {
      if (wrapperRef.current?.contains(e.target as Node) === true) return;
      close();
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open, close]);

  // Keep the highlighted option in view. Optional-called: jsdom does not
  // implement it, and a missing scroll must never throw out of an effect.
  useEffect(() => {
    if (!open) return;
    const el = wrapperRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    el?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, open]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
        return;
      }
      // Tab closes and lets focus move on naturally. Deliberately NOT the
      // modal's focus trap: a popover does not own the page, and trapping Tab
      // in a topbar field would strand keyboard users in the chrome.
      if (e.key === "Tab") {
        close();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        if (!open) {
          setOpen(true);
          return;
        }
        if (flatRows.length > 0) setActiveIndex((i) => (i + 1) % flatRows.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        if (flatRows.length > 0) {
          setActiveIndex((i) => (i - 1 + flatRows.length) % flatRows.length);
        }
        return;
      }
      if (e.key === "Home" && flatRows.length > 0) {
        e.preventDefault();
        setActiveIndex(0);
        return;
      }
      if (e.key === "End" && flatRows.length > 0) {
        e.preventDefault();
        setActiveIndex(flatRows.length - 1);
        return;
      }
      if (e.key === "Enter") {
        const row = flatRows[activeIndex];
        if (row !== undefined) {
          e.preventDefault();
          go(row.href);
        }
      }
    },
    [open, flatRows, activeIndex, go, close],
  );

  const activeRow = flatRows[activeIndex];

  return (
    /*
     * `relative` is the popover's positioning context; this wrapper carries the
     * canonical .cmdk box styling the old <div role="button"> had, and
     * `ml-auto` still anchors the topbar's right cluster.
     */
    <div
      ref={wrapperRef}
      className={[
        "relative ml-auto flex shrink-0 items-center",
        "rounded-[var(--radius,0.5rem)] border border-border-default",
        "bg-surface-elevated text-text-secondary",
        "transition-[border-color] hover:border-border-focus",
        "focus-within:border-border-focus",
      ].join(" ")}
      style={{ width: "256px", height: "34px", padding: "0 10px", gap: "8px", fontSize: "13px" }}
      data-testid="search-trigger"
      // The whole 256×34 box reads as one control, so a click anywhere in it —
      // the magnifier, the ⌘K chip, the padding — puts the caret in the input.
      // preventDefault stops the mousedown stealing focus back to the wrapper.
      onMouseDown={(e) => {
        if (e.target !== inputRef.current) {
          e.preventDefault();
          inputRef.current?.focus();
          setOpen(true);
        }
      }}
    >
      <SearchIcon />
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={LISTBOX_ID}
        aria-activedescendant={
          open && activeRow !== undefined ? `${OPTION_ID_PREFIX}${activeRow.id}` : undefined
        }
        aria-autocomplete="list"
        aria-label="Search or jump to…"
        placeholder="Search or jump to…"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        className={[
          "min-w-0 flex-1 border-0 bg-transparent p-0",
          "text-text-primary placeholder-text-secondary",
          "focus:outline-none focus:ring-0",
        ].join(" ")}
        style={{ fontSize: "13px" }}
        data-testid="search-input"
      />
      {/* .kbd chip — margin-left:auto inside the cmdk */}
      <span
        className="ml-auto inline-flex shrink-0 items-center rounded-[5px] border border-border-default bg-surface-subtle text-text-secondary"
        style={{ fontSize: "10.5px", fontWeight: 600, padding: "1px 5px", lineHeight: 1.4 }}
        aria-hidden
      >
        ⌘K
      </span>

      {open ? (
        <SearchResultsController
          query={query}
          debounced={debounced}
          base={base}
          activeIndex={activeIndex}
          onRowsChange={setFlatRows}
          onHoverRow={setActiveIndex}
          onSelectRow={selectRow}
        />
      ) : null}
    </div>
  );
}

/**
 * 15×15 magnifier. Moved here from `AppShellV2` with the affordance that was
 * its only consumer.
 */
function SearchIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      width={15}
      height={15}
      className="shrink-0"
      aria-hidden
    >
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}
